export interface TeamShareOption {
  id: string;
  name: string;
  provider: string;
  color?: string;
}

export interface TeamInvitePreviewMember {
  userId: string;
  name: string;
  photoUrl: string;
}

export interface ParsedInviteMeta {
  teamName: string;
  inviteRole: 'admin' | 'member';
  inviterName: string;
  previewMembers: TeamInvitePreviewMember[];
  memberSummaryText: string;
  isViewerEligible: boolean;
  viewerReason: string;
}

export interface TeamCalendarOptionSource {
  calendarId: string;
  name?: string;
  calendarType?: string;
  color?: string;
}

export interface TeamTaskBoardOptionSource {
  todoTaskId: string;
  name?: string;
  appFrom?: string;
}
type TeamTFunction = (
  key: string,
  options?: Record<string, unknown> & { defaultValue?: string }
) => string;

const isRecord = (value: unknown): value is Record<string, unknown> => {
  return typeof value === 'object' && value !== null;
};

const parseString = (value: unknown) => {
  return typeof value === 'string' ? value.trim() : '';
};

const parseCount = (value: unknown) => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === 'string') {
    const parsedValue = Number(value);
    return Number.isFinite(parsedValue) ? parsedValue : 0;
  }

  return 0;
};

const parsePreviewMembers = (value: unknown) => {
  if (!Array.isArray(value)) {
    return [] as TeamInvitePreviewMember[];
  }

  const seenMemberKeys: Record<string, boolean> = {};
  const previewMembers: TeamInvitePreviewMember[] = [];

  value.forEach((item, index) => {
    if (!isRecord(item)) {
      return;
    }

    const userId =
      parseString(item.userId) ||
      parseString(item.id) ||
      parseString(item.teamMemberId) ||
      `member-${index + 1}`;
    const name = parseString(item.name) || parseString(item.displayName);
    const photoUrl = parseString(item.photoUrl) || parseString(item.avatarUrl);
    const dedupeKey = `${userId}|${name}|${photoUrl}`;

    if (seenMemberKeys[dedupeKey]) {
      return;
    }

    seenMemberKeys[dedupeKey] = true;
    previewMembers.push({
      userId,
      name,
      photoUrl,
    });
  });

  return previewMembers;
};

const buildInviteMemberSummary = (
  members: TeamInvitePreviewMember[],
  totalCount?: number,
  t?: TeamTFunction
) => {
  const names = members
    .map((member) => member.name.trim())
    .filter((name) => Boolean(name))
    .slice(0, 3);
  const resolvedCount = totalCount && totalCount > 0 ? totalCount : members.length;

  if (names.length) {
    if (resolvedCount > names.length) {
      const extraCount = resolvedCount - names.length;
      const nameText = names.join(', ');

      return t
        ? t('team.acceptInvite.memberSummaryWithExtra', {
            names: nameText,
            count: extraCount,
          })
        : `${nameText} and ${extraCount} ${extraCount === 1 ? 'other' : 'others'} are already on the team.`;
    }

    if (names.length === 1) {
      return t
        ? t('team.acceptInvite.memberSummaryOneName', { name: names[0] })
        : `${names[0]} is already on the team.`;
    }

    return t
      ? t('team.acceptInvite.memberSummaryNames', { names: names.join(', ') })
      : `${names.join(', ')} are already on the team.`;
  }

  if (resolvedCount === 1) {
    return t
      ? t('team.acceptInvite.memberSummaryCount', { count: 1 })
      : '1 teammate is already on the team.';
  }

  if (resolvedCount > 1) {
    return t
      ? t('team.acceptInvite.memberSummaryCount', { count: resolvedCount })
      : `${resolvedCount} teammates are already on the team.`;
  }

  return '';
};

const getUniqueIds = (ids: string[]) => {
  const seenMap: Record<string, boolean> = {};
  const uniqueIds: string[] = [];

  ids.forEach((id) => {
    if (!seenMap[id]) {
      seenMap[id] = true;
      uniqueIds.push(id);
    }
  });

  return uniqueIds;
};

export const parseInviteMeta = (resolvedInvite: unknown, t?: TeamTFunction): ParsedInviteMeta => {
  const parsedResult: ParsedInviteMeta = {
    teamName: '',
    inviteRole: 'member',
    inviterName: '',
    previewMembers: [],
    memberSummaryText: '',
    isViewerEligible: true,
    viewerReason: '',
  };

  if (!isRecord(resolvedInvite)) {
    return parsedResult;
  }

  const invitePayload = isRecord(resolvedInvite.invite) ? resolvedInvite.invite : {};
  const teamPayload = isRecord(resolvedInvite.team) ? resolvedInvite.team : {};
  const viewerPayload = isRecord(resolvedInvite.viewer) ? resolvedInvite.viewer : {};

  parsedResult.teamName = parseString(teamPayload.name);

  const inviteRole = parseString(invitePayload.role).toLowerCase();
  if (inviteRole === 'admin' || inviteRole === 'member') {
    parsedResult.inviteRole = inviteRole;
  }

  parsedResult.inviterName =
    parseString(invitePayload.inviterName) ||
    parseString(invitePayload.invitedByName) ||
    parseString(viewerPayload.inviterName) ||
    parseString((resolvedInvite as Record<string, unknown>).inviterName);

  const previewCandidates = [
    teamPayload.members,
    teamPayload.teamMembers,
    teamPayload.memberPreview,
    invitePayload.members,
    invitePayload.teamMembers,
    invitePayload.memberPreview,
  ];

  previewCandidates.forEach((candidate) => {
    if (parsedResult.previewMembers.length >= 6) {
      return;
    }

    const nextMembers = parsePreviewMembers(candidate).slice(
      0,
      6 - parsedResult.previewMembers.length
    );
    parsedResult.previewMembers = [...parsedResult.previewMembers, ...nextMembers];
  });

  const memberSummaryText =
    parseString(teamPayload.memberSummary) ||
    parseString(teamPayload.memberSummaryText) ||
    parseString(invitePayload.memberSummary) ||
    parseString(invitePayload.memberSummaryText);
  const memberCount =
    parseCount(teamPayload.memberCount) ||
    parseCount(invitePayload.memberCount) ||
    parseCount(viewerPayload.memberCount);

  parsedResult.memberSummaryText =
    memberSummaryText || buildInviteMemberSummary(parsedResult.previewMembers, memberCount, t);
  parsedResult.isViewerEligible = viewerPayload.isEligible !== false;
  parsedResult.viewerReason = parseString(viewerPayload.reason);

  return parsedResult;
};

export const getInviteCodeFromSearch = (search: string, queryKeys: string[]) => {
  const searchParams = new URLSearchParams(search);
  const inviteCodeFromQuery = queryKeys
    .map((key) => {
      return searchParams.get(key)?.trim() || '';
    })
    .find(Boolean);

  return inviteCodeFromQuery || null;
};

export const buildIdLookup = (ids: string[]) => {
  const idLookup: Record<string, boolean> = {};

  ids.forEach((id) => {
    idLookup[id] = true;
  });

  return idLookup;
};

export const getSharedCount = (
  sharedIds: string[],
  availableIdsLookup: Record<string, boolean>,
  isOptionsLoading: boolean
) => {
  const uniqueSharedIds = getUniqueIds(sharedIds);

  if (isOptionsLoading) {
    return uniqueSharedIds.length;
  }

  if (!Object.keys(availableIdsLookup).length) {
    return 0;
  }

  return uniqueSharedIds.filter((id) => Boolean(availableIdsLookup[id])).length;
};

export const sanitizeSelection = (
  ids: string[],
  availableIdsLookup: Record<string, boolean>,
  isOptionsLoading: boolean
) => {
  const normalizedIds = getUniqueIds(ids);

  if (isOptionsLoading) {
    return normalizedIds;
  }

  if (!Object.keys(availableIdsLookup).length) {
    return [];
  }

  return normalizedIds.filter((id) => Boolean(availableIdsLookup[id]));
};

export const areEqualIds = (left: string[], right: string[]) => {
  if (left.length !== right.length) {
    return false;
  }

  return left.every((id, index) => id === right[index]);
};

export const getCalendarProviderLabel = (calendarType?: string, t?: TeamTFunction) => {
  if (calendarType === 'google') {
    return 'Google Calendar';
  }

  if (calendarType === 'outlook') {
    return 'Outlook Calendar';
  }

  if (calendarType === 'apple') {
    return 'Apple Calendar';
  }

  if (calendarType === 'lifestack') {
    return 'Lifestack Calendar';
  }

  return t ? t('team.common.calendar') : 'Calendar';
};

export const getTaskProviderLabel = (appFrom?: string, t?: TeamTFunction) => {
  if (appFrom === 'todoist') {
    return 'Todoist';
  }

  if (appFrom === 'trello') {
    return 'Trello';
  }

  if (appFrom === 'ticktick') {
    return 'TickTick';
  }

  if (appFrom === 'clickup') {
    return 'ClickUp';
  }

  if (appFrom === 'github') {
    return 'GitHub';
  }

  if (appFrom === 'notion') {
    return 'Notion';
  }

  if (appFrom === 'googleTasks') {
    return 'Google Tasks';
  }

  if (appFrom === 'google-tasks') {
    return 'Google Tasks';
  }

  if (appFrom === 'apple-reminders') {
    return 'Apple Reminders';
  }

  if (appFrom === 'lifestack') {
    return 'Lifestack';
  }

  return t ? t('team.common.board') : 'Board';
};

export const buildInviteCalendarOptions = (
  calendarsMeta: TeamCalendarOptionSource[],
  t?: TeamTFunction
) => {
  return calendarsMeta
    .filter((calendar) => Boolean(calendar.calendarId))
    .map((calendar) => {
      return {
        id: calendar.calendarId,
        name:
          calendar.name?.trim() || (t ? t('team.common.untitledCalendar') : 'Untitled calendar'),
        provider: getCalendarProviderLabel(calendar.calendarType, t),
        color: calendar.color || 'var(--color-primary, #680ddb)',
      } as TeamShareOption;
    });
};

export const buildInviteTaskBoardOptions = (
  todoProjects: TeamTaskBoardOptionSource[],
  t?: TeamTFunction
) => {
  return todoProjects
    .filter((board) => Boolean(board.todoTaskId))
    .map((board) => {
      return {
        id: board.todoTaskId,
        name: board.name?.trim() || (t ? t('team.common.untitledBoard') : 'Untitled board'),
        provider: getTaskProviderLabel(board.appFrom, t),
      } as TeamShareOption;
    });
};

export const isTeamNotFoundError = (errorMessage: string) => {
  const normalizedError = errorMessage.trim().toLowerCase();
  return normalizedError.includes('team not found') || normalizedError === 'not found';
};
